import {
  ChangeStatusRequest,
  CreateDocumentRequest,
  CreateNewVersionRequest,
  DocumentAcknowledgmentResponse,
  DocumentComplianceMatrixQuery,
  DocumentComplianceMatrixResponse,
  DocumentSummaryResponse,
  DocumentVersionResponse,
  DocumentViewResponse,
  ListDocumentsQuery,
  PendingAcknowledgmentResponse,
  PublishDocumentRequest,
  SaveDraftVersionRequest,
  UpdateDocumentMetadataRequest,
} from 'hvp-shared';
import hvpAuthenticatedApi from '../../../../api/hvpAuthenticatedApi';

const BASE = '/documents';

export interface DocumentDetailPayload {
  document: DocumentViewResponse;
  currentVersion: DocumentVersionResponse | null;
}

export async function fetchDocuments(
  query: ListDocumentsQuery
): Promise<DocumentSummaryResponse[]> {
  const params: Record<string, string | number | boolean | undefined> = {
    category: query.category,
    type: query.type,
    status: query.status,
    criticality: query.criticality,
    tags: query.tags,
    search: query.search,
    requiredForOnboarding: query.requiredForOnboarding,
    requiresAcknowledgment: query.requiresAcknowledgment,
    pendingForCurrentUser: query.pendingForCurrentUser,
    includeWithdrawn: query.includeWithdrawn,
    limit: query.limit,
    skip: query.skip,
  };
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  );
  const resp = await hvpAuthenticatedApi.get(BASE, { params: cleanParams });
  return resp.data.data as DocumentSummaryResponse[];
}

export async function fetchDocumentBySlug(
  slug: string
): Promise<DocumentDetailPayload> {
  const resp = await hvpAuthenticatedApi.get(`${BASE}/${slug}`);
  return resp.data.data as DocumentDetailPayload;
}

export async function searchDocuments(
  q: string,
  limit?: number,
  options?: { includeWithdrawn?: boolean }
): Promise<DocumentSummaryResponse[]> {
  const resp = await hvpAuthenticatedApi.get(`${BASE}/search`, {
    params: {
      q,
      limit,
      includeWithdrawn: options?.includeWithdrawn || undefined,
    },
  });
  return resp.data.data as DocumentSummaryResponse[];
}

// ─── Write + workflow ────────────────────────────────────────────────────────

export async function createDocument(
  body: CreateDocumentRequest
): Promise<DocumentDetailPayload> {
  const resp = await hvpAuthenticatedApi.post(BASE, body);
  return resp.data.data as DocumentDetailPayload;
}

export async function updateDocumentMetadata(
  id: string,
  body: UpdateDocumentMetadataRequest
): Promise<DocumentViewResponse> {
  const resp = await hvpAuthenticatedApi.patch(`${BASE}/${id}`, body);
  return resp.data.data as DocumentViewResponse;
}

export async function saveDraftVersion(
  id: string,
  body: SaveDraftVersionRequest
): Promise<DocumentVersionResponse> {
  const resp = await hvpAuthenticatedApi.patch(
    `${BASE}/${id}/versions/draft`,
    body
  );
  return resp.data.data as DocumentVersionResponse;
}

export async function createNewVersion(
  id: string,
  body: CreateNewVersionRequest
): Promise<DocumentVersionResponse> {
  const resp = await hvpAuthenticatedApi.post(`${BASE}/${id}/versions`, body);
  return resp.data.data as DocumentVersionResponse;
}

export async function listDocumentVersions(
  id: string
): Promise<DocumentVersionResponse[]> {
  const resp = await hvpAuthenticatedApi.get(`${BASE}/${id}/versions`);
  return resp.data.data as DocumentVersionResponse[];
}

export async function submitForReview(
  id: string
): Promise<DocumentVersionResponse> {
  const resp = await hvpAuthenticatedApi.post(
    `${BASE}/${id}/submit-for-review`
  );
  return resp.data.data as DocumentVersionResponse;
}

export async function publishDocument(
  id: string,
  body: PublishDocumentRequest = {}
): Promise<DocumentDetailPayload> {
  const resp = await hvpAuthenticatedApi.post(`${BASE}/${id}/publish`, body);
  return resp.data.data as DocumentDetailPayload;
}

export async function changeDocumentStatus(
  id: string,
  body: ChangeStatusRequest
): Promise<DocumentViewResponse> {
  const resp = await hvpAuthenticatedApi.post(`${BASE}/${id}/status`, body);
  return resp.data.data as DocumentViewResponse;
}

// ─── Acknowledgments (SP4) ───────────────────────────────────────────────────

export async function acknowledgeDocument(
  id: string
): Promise<DocumentAcknowledgmentResponse> {
  const resp = await hvpAuthenticatedApi.post(`${BASE}/${id}/acknowledge`);
  return resp.data.data as DocumentAcknowledgmentResponse;
}

export async function fetchPendingAcknowledgments(): Promise<
  PendingAcknowledgmentResponse[]
> {
  const resp = await hvpAuthenticatedApi.get(`${BASE}/me/pending-acknowledgments`);
  return resp.data.data as PendingAcknowledgmentResponse[];
}

export async function fetchAcknowledgmentsForDocument(
  id: string
): Promise<DocumentAcknowledgmentResponse[]> {
  const resp = await hvpAuthenticatedApi.get(`${BASE}/${id}/acknowledgments`);
  return resp.data.data as DocumentAcknowledgmentResponse[];
}

// ─── Compliance matrix (admin/manager) ────────────────────────────────────

function complianceParams(
  query: DocumentComplianceMatrixQuery
): Record<string, string> {
  const out: Record<string, string> = {};
  if (query.documentIds?.length) out.documentIds = query.documentIds.join(',');
  if (query.collaboratorIds?.length)
    out.collaboratorIds = query.collaboratorIds.join(',');
  if (query.category) out.category = query.category;
  if (query.audienceRole) out.audienceRole = query.audienceRole;
  return out;
}

export async function fetchComplianceMatrix(
  query: DocumentComplianceMatrixQuery
): Promise<DocumentComplianceMatrixResponse> {
  const resp = await hvpAuthenticatedApi.get(
    `${BASE}/acknowledgments/matrix`,
    { params: complianceParams(query) }
  );
  return resp.data.data as DocumentComplianceMatrixResponse;
}

export async function downloadComplianceMatrixXlsx(
  query: DocumentComplianceMatrixQuery
): Promise<Blob> {
  const resp = await hvpAuthenticatedApi.get(
    `${BASE}/acknowledgments/matrix/export`,
    { params: complianceParams(query), responseType: 'blob' }
  );
  return resp.data as Blob;
}
